import { useRef } from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export type TabId = "upload" | "status" | "verify";

export type TabDef = {
  id: TabId;
  label: string;
};

export const TAB_ITEMS: readonly TabDef[] = [
  { id: "upload", label: "Carica PDF" },
  { id: "status", label: "Stato Analisi" },
  { id: "verify", label: "Verifica Fatture-Polizze" },
] as const;
const TAB_IDS = TAB_ITEMS.map((item) => item.id);

export function getNextTabId(current: TabId, key: string): TabId {
  const currentIndex = TAB_IDS.indexOf(current);

  if (key === "ArrowRight") {
    const nextIndex = (currentIndex + 1) % TAB_IDS.length;
    return TAB_IDS[nextIndex] ?? current;
  }

  if (key === "ArrowLeft") {
    const previousIndex = (currentIndex - 1 + TAB_IDS.length) % TAB_IDS.length;
    return TAB_IDS[previousIndex] ?? current;
  }

  if (key === "Home") {
    return TAB_IDS[0] ?? current;
  }

  if (key === "End") {
    return TAB_IDS[TAB_IDS.length - 1] ?? current;
  }

  return current;
}

type TabNavigationProps = {
  activeTab: TabId;
  onTabChange: (tabId: TabId) => void;
};

export function TabNavigation({ activeTab, onTabChange }: TabNavigationProps) {
  const tabRefs = useRef<Record<TabId, HTMLButtonElement | null>>({
    upload: null,
    status: null,
    verify: null,
  });

  function applyKeyboardMove(key: string, currentTab: TabId) {
    const nextTab = getNextTabId(currentTab, key);
    if (nextTab === currentTab) {
      return;
    }

    onTabChange(nextTab);
    requestAnimationFrame(() => {
      tabRefs.current[nextTab]?.focus();
    });
  }

  return (
    <div
      className="tab-navigation"
      role="tablist"
      aria-label="Schede flusso principale"
    >
      {TAB_ITEMS.map((tab) => {
        const isActive = tab.id === activeTab;

        return (
          <Button
            key={tab.id}
            ref={(element) => {
              tabRefs.current[tab.id] = element;
            }}
            role="tab"
            id={`tab-${tab.id}`}
            aria-controls={`panel-${tab.id}`}
            aria-selected={isActive}
            tabIndex={isActive ? 0 : -1}
            className={cn(
              "tab-trigger",
              isActive ? "tab-trigger--active" : "tab-trigger--inactive",
            )}
            onClick={() => onTabChange(tab.id)}
            onKeyDown={(event) => {
              const navigable = ["ArrowLeft", "ArrowRight", "Home", "End"];
              if (navigable.includes(event.key)) {
                event.preventDefault();
                applyKeyboardMove(event.key, tab.id);
              }
            }}
            type="button"
            variant="ghost"
          >
            {tab.label}
          </Button>
        );
      })}
    </div>
  );
}
